import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { login, logout } from "../api/loginApis";
import { getUserPermissionByUser } from "../api/workOrder";
import {
  canReadAccess,
  canWriteAccess,
  normalizeAccess,
  ROUTE_REQUIREMENTS,
} from "../constants/permissionCatalog";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("isAuthenticated") === "true";
  });
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [permissions, setPermissions] = useState(() => {
    const stored = localStorage.getItem("permissions");
    return stored ? JSON.parse(stored) : {};
  });
  const [permissionsLoading, setPermissionsLoading] = useState(() => {
    return !localStorage.getItem("permissions");
  });
  const permissionsFetchedRef = useRef(false);

  useEffect(() => {
    const stored = localStorage.getItem("permissions");
    if (stored) {
      try {
        setPermissions(JSON.parse(stored));
      } catch (e) {
        console.warn("Invalid stored permissions, clearing...");
        localStorage.removeItem("permissions");
      }
    }
  }, []);

  const fetchPermissions = useCallback(async (force = false) => {
    if (!isAuthenticated || !user?._id) {
      setPermissionsLoading(false);
      return;
    }

    if (permissionsFetchedRef.current && !force) return;
    permissionsFetchedRef.current = true;
    if (!force) {
      setPermissionsLoading(true);
    }

    try {
      const res = await getUserPermissionByUser(user._id);

      const permissionMap = {};
      res.data?.permissions?.forEach((p) => {
        permissionMap[p.resource] = normalizeAccess(
          p.access ?? p.allowed ?? "no_access",
        );
      });

      setPermissions(permissionMap);
      localStorage.setItem("permissions", JSON.stringify(permissionMap));
      return permissionMap;
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
    } finally {
      setPermissionsLoading(false);
    }
  }, [isAuthenticated, user?._id]);

  const refreshPermissions = useCallback(() => fetchPermissions(true), [fetchPermissions]);

  const notifyPermissionsChanged = useCallback(() => {
    fetchPermissions(true);

    try {
      if (typeof window !== "undefined" && "BroadcastChannel" in window) {
        const channel = new BroadcastChannel("erpag_permissions_channel");
        channel.postMessage({ type: "PERMISSIONS_UPDATED", timestamp: Date.now() });
        channel.close();
      }
    } catch (e) {}

    try {
      localStorage.setItem("erpag_permissions_sync_trigger", String(Date.now()));
    } catch (e) {}

    try {
      window.dispatchEvent(new CustomEvent("erpag_permissions_updated"));
    } catch (e) {}
  }, [fetchPermissions]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Real-time synchronization: cross-tab, tab focus, visibility change, and heartbeat
  useEffect(() => {
    if (!isAuthenticated || !user?._id) return;

    let lastFetch = Date.now();
    const debouncedRefresh = () => {
      const now = Date.now();
      if (now - lastFetch > 2500) {
        lastFetch = now;
        fetchPermissions(true);
      }
    };

    // 1. Re-sync on tab focus or visibility change
    const onFocus = () => debouncedRefresh();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        debouncedRefresh();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    // 2. Cross-tab BroadcastChannel listener
    let channel = null;
    try {
      if (typeof window !== "undefined" && "BroadcastChannel" in window) {
        channel = new BroadcastChannel("erpag_permissions_channel");
        channel.onmessage = (event) => {
          if (event?.data?.type === "PERMISSIONS_UPDATED") {
            debouncedRefresh();
          }
        };
      }
    } catch (e) {}

    // 3. Storage event listener (cross-tab fallback)
    const onStorage = (e) => {
      if (e.key === "erpag_permissions_sync_trigger") {
        debouncedRefresh();
      }
    };
    window.addEventListener("storage", onStorage);

    // 4. Custom in-tab event listener
    const onCustom = () => debouncedRefresh();
    window.addEventListener("erpag_permissions_updated", onCustom);

    // 5. Periodic Heartbeat (every 15 seconds while tab is active)
    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        debouncedRefresh();
      }
    }, 15000);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("erpag_permissions_updated", onCustom);
      if (channel) {
        channel.close();
      }
      clearInterval(intervalId);
    };
  }, [isAuthenticated, user?._id, fetchPermissions]);

  const handleLogin = async (email, password) => {
    setIsLoading(true);
    try {
      const response = await login(email, password);

      const userData = {
        ...response.user,
        isSetupComplete: response.user.isSetupComplete ?? false,
        mustChangePassword: response.user.mustChangePassword ?? false,
      };

      localStorage.setItem("token", response.token);
      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem("user", JSON.stringify(userData));
      setIsAuthenticated(true);
      setUser(userData);

      permissionsFetchedRef.current = false;
      setPermissionsLoading(true);

      return { success: true, user: userData };
    } catch (error) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("isAuthenticated");
      throw new Error(
        error.response?.data?.message || "Invalid email or password",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("user");
      localStorage.removeItem("permissions");
      setIsAuthenticated(false);
      setUser(null);
      setPermissions({});
      setPermissionsLoading(false);
      permissionsFetchedRef.current = false;
    }
  };

  const updateUser = (updatedUser) => {
    const normalized = {
      ...updatedUser,
      isSetupComplete: updatedUser.isSetupComplete ?? false,
      mustChangePassword: updatedUser.mustChangePassword ?? false,
    };

    setUser(normalized);
    localStorage.setItem("user", JSON.stringify(normalized));
  };

  const hasPermission = (key) => {
    return canReadAccess(permissions[key]);
  };

  const getAccess = (key) => normalizeAccess(permissions[key]);
  const canRead = (key) => canReadAccess(permissions[key]);
  const canWrite = (key) => canWriteAccess(permissions[key]);
  const canAccessRoute = (path) => {
    const req = ROUTE_REQUIREMENTS[path];
    if (!req) return true;
    return canRead(req.module) && canRead(req.page);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        permissions,
        hasPermission,
        getAccess,
        canRead,
        canWrite,
        canAccessRoute,
        permissionsLoading,
        refreshPermissions,
        notifyPermissionsChanged,
        login: handleLogin,
        logout: handleLogout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
